/**
 * Contacts API service for managing contacts.
 * @module lib/api/contacts.api
 */

import { useOrganizationStore } from '@/store/organization.store'

import {
  BulkOperationResult,
  BulkUpdateContacts,
  Contact,
  CreateContact,
  createPaginationMeta,
  ExportOptions,
  ImportOptions,
  ImportPreviewRecord,
  ImportResult,
  PaginatedResponse,
  PaginationParams,
  UpdateContact,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { createSingletonApi } from './singleton-factory'

export interface ContactEntityLink {
  id: string
  contactId: string
  entityId: string
  isPrimary: boolean
  role?: string | null
  title?: string | null
  department?: string | null
  notes?: string | null
  entity?: {
    id: string
    name: string
    logoUrl?: string | null
  } | null
}

export interface AddContactEntityLinkPayload {
  entityId: string
  isPrimary?: boolean
  role?: string | null
  title?: string | null
  department?: string | null
  notes?: string | null
}

class ContactApiService extends BaseApi<Contact, CreateContact, UpdateContact> {
  constructor() {
    super({
      baseEndpoint: '/contacts',
      responseNormalization: true,
    })
  }

  // Override create to add organization ID
  async create(data: CreateContact): Promise<Contact> {
    const { currentOrganization } = useOrganizationStore.getState()

    const contactData = this.addToOrganizationData(
      data,
      currentOrganization?.id,
    )

    return super.create(contactData as CreateContact)
  }

  // Alias methods for backward compatibility
  async getContacts(
    params: Partial<
      PaginationParams & {
        entityId?: string
        supplierId?: string
        moduleType?: string
        moduleId?: string
        name?: string
        lifecycleStage?: string
        vendorStage?: string
        relationshipTypeIds?: string[] | string
        relationshipTypeKeys?: string[] | string
        relationshipStageIds?: string[] | string
        source?: string
        companyName?: string
        // T2 record-state opt-in: 'all' or an explicit state; unset = server
        // default (active + provisional, tombstones hidden).
        recordState?: string
        // ScQL query string (Jira-style) — ANDed with the other filters
        query?: string
        columnFilters?: Record<string, unknown>
      }
    > = {},
  ): Promise<PaginatedResponse<Contact>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.set('page', String(params.page))
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.sortBy) searchParams.set('sortBy', params.sortBy)
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)
    if (params.entityId) searchParams.set('entityId', params.entityId)
    if (params.supplierId) searchParams.set('supplierId', params.supplierId)
    if (params.lifecycleStage)
      searchParams.set('lifecycleStage', params.lifecycleStage)
    if (params.vendorStage)
      searchParams.set('vendorStage', params.vendorStage)
    const appendListParam = (key: string, value?: string[] | string) => {
      if (!value) return
      const values = Array.isArray(value) ? value : [value]
      const normalized = values.map(item => item.trim()).filter(Boolean)
      if (normalized.length > 0) searchParams.set(key, normalized.join(','))
    }
    appendListParam('relationshipTypeIds', params.relationshipTypeIds)
    appendListParam('relationshipTypeKeys', params.relationshipTypeKeys)
    appendListParam('relationshipStageIds', params.relationshipStageIds)
    if (params.source) searchParams.set('source', params.source)
    if (params.companyName) searchParams.set('companyName', params.companyName)
    if (params.recordState) searchParams.set('recordState', params.recordState)
    if (params.query) searchParams.set('query', params.query)
    // Polymorphic parent scope — used to list contacts linked to any module
    // (e.g. custom collection entry, project, deal).
    if (params.moduleType) searchParams.set('moduleType', params.moduleType)
    if (params.moduleId) searchParams.set('moduleId', params.moduleId)
    if (params.name) searchParams.set('name', params.name)
    if (params.columnFilters && Object.keys(params.columnFilters).length > 0) {
      searchParams.set('columnFilters', JSON.stringify(params.columnFilters))
    }

    const queryString = searchParams.toString()
    return this.makeRequest<PaginatedResponse<Contact>>(
      `${this.config.baseEndpoint}?${queryString}`,
      {
        method: 'GET',
      },
    )
  }

  async getContact(id: string): Promise<Contact> {
    return this.findById(id)
  }

  async createContact(data: CreateContact): Promise<Contact> {
    return this.create(data)
  }

  async updateContact(id: string, data: UpdateContact): Promise<Contact> {
    return this.update(id, data)
  }

  async deleteContact(id: string): Promise<void> {
    return this.delete(id)
  }

  async uploadContactImage(
    contactId: string,
    file: File,
  ): Promise<{ imageUrl: string }> {
    const formData = new FormData()
    formData.append('file', file)
    return this.makeRequest<{ imageUrl: string }>(
      `${this.config.baseEndpoint}/${contactId}/image`,
      {
        method: 'POST',
        body: formData,
      },
    )
  }

  async getContactImage(
    contactId: string,
  ): Promise<{ imageUrl: string | null }> {
    return this.makeRequest<{ imageUrl: string | null }>(
      `${this.config.baseEndpoint}/${contactId}/image`,
    )
  }

  async removeContactImage(contactId: string): Promise<Contact> {
    return this.makeRequest<Contact>(
      `${this.config.baseEndpoint}/${contactId}/image`,
      {
        method: 'DELETE',
      },
    )
  }

  /**
   * Bulk field update. Returns the shared bulk-result envelope — a partial
   * success is a 200 with `failures`, NOT an error, so callers must read the
   * counts rather than assuming every id in the request was changed.
   */
  async bulkUpdateContacts(
    ids: string[],
    updates: BulkUpdateContacts,
  ): Promise<BulkOperationResult> {
    return this.makeRequest<BulkOperationResult>(
      `${this.config.baseEndpoint}/bulk-update`,
      {
        method: 'POST',
        body: { ids, updates },
      },
    )
  }

  async bulkDeleteContacts(ids: string[]): Promise<{ deletedCount: number }> {
    return this.makeRequest<{ deletedCount: number }>(
      `${this.config.baseEndpoint}/bulk-delete`,
      {
        method: 'POST',
        body: { ids },
      },
    )
  }

  /**
   * Vouch for captured contacts: provisional -> active. Also promotes the
   * company each was captured alongside.
   */
  async confirmProvisionalContacts(
    ids: string[],
  ): Promise<{ confirmedContacts: number; confirmedEntities: number }> {
    return this.makeRequest<{
      confirmedContacts: number
      confirmedEntities: number
    }>(`${this.config.baseEndpoint}/confirm-provisional`, {
      method: 'POST',
      body: { ids },
    })
  }

  async recalculateScores(): Promise<{ updated: number }> {
    return this.makeRequest<{ updated: number }>(
      `${this.config.baseEndpoint}/recalculate-scores`,
      {
        method: 'POST',
      },
    )
  }

  async getEntityLinks(contactId: string): Promise<ContactEntityLink[]> {
    return this.makeRequest<ContactEntityLink[]>(
      `${this.config.baseEndpoint}/${contactId}/entities`,
    )
  }

  async addEntityLink(
    contactId: string,
    data: AddContactEntityLinkPayload,
  ): Promise<ContactEntityLink> {
    return this.makeRequest<ContactEntityLink>(
      `${this.config.baseEndpoint}/${contactId}/entities`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  async setEntityLinkPrimary(
    contactId: string,
    entityId: string,
  ): Promise<ContactEntityLink> {
    return this.makeRequest<ContactEntityLink>(
      `${this.config.baseEndpoint}/${contactId}/entities/${entityId}/primary`,
      {
        method: 'PUT',
      },
    )
  }

  async removeEntityLink(contactId: string, entityId: string): Promise<void> {
    await this.makeRequest<void>(
      `${this.config.baseEndpoint}/${contactId}/entities/${entityId}`,
      {
        method: 'DELETE',
      },
    )
  }

  // Get contacts for a specific entity
  async getContactsByEntity(
    entityId: string,
    params: Partial<PaginationParams> = {},
  ): Promise<PaginatedResponse<Contact>> {
    const searchParams = new URLSearchParams()
    if (params.page) searchParams.set('page', String(params.page))
    if (params.limit) searchParams.set('limit', String(params.limit))
    if (params.sortBy) searchParams.set('sortBy', params.sortBy)
    if (params.sortOrder) searchParams.set('sortOrder', params.sortOrder)
    searchParams.set('entityId', entityId)

    const queryString = searchParams.toString()
    return this.makeRequest<PaginatedResponse<Contact>>(
      `${this.config.baseEndpoint}?${queryString}`,
      {
        method: 'GET',
      },
    )
  }

  // Compatibility wrapper for old lead routes. Leads are now contacts, so the
  // old lead id is resolved through the migration mapping before reading.
  async getContactsByLead(
    leadId: string,
    params: Partial<PaginationParams> = {},
  ): Promise<PaginatedResponse<Contact>> {
    const page = params.page ?? 1
    const limit = params.limit ?? 50
    const resolved = await this.makeRequest<{ contactId: string | null }>(
      `${this.config.baseEndpoint}/resolve-lead/${leadId}`,
      {
        method: 'GET',
      },
    )

    if (!resolved.contactId) {
      return { data: [], meta: createPaginationMeta(page, limit, 0) }
    }

    const contact = await this.findById(resolved.contactId)
    return { data: [contact], meta: createPaginationMeta(page, limit, 1) }
  }

  // Import/Export operations
  previewImport(
    records: unknown[],
    options: ImportOptions,
  ): Promise<ImportPreviewRecord[]> {
    return this.makeRequest<ImportPreviewRecord[]>(
      `${this.config.baseEndpoint}/import/preview`,
      {
        method: 'POST',
        body: { records, options },
      },
    )
  }

  import(data: {
    records: ImportPreviewRecord[]
    options: ImportOptions
  }): Promise<ImportResult> {
    return this.makeRequest<ImportResult>(
      `${this.config.baseEndpoint}/import`,
      {
        method: 'POST',
        body: data,
      },
    )
  }

  export(options: ExportOptions): Promise<Contact[]> {
    return this.makeRequest<Contact[]>(`${this.config.baseEndpoint}/export`, {
      method: 'POST',
      body: options,
    })
  }
}

// Export singleton instance using factory
export const contactApi = createSingletonApi(ContactApiService)

// Export for backward compatibility
export { contactApi as ContactApi }
